import { createContext, useContext, useState } from "react";
import { useQuery } from "@tanstack/react-query"; // Import TanStack Query
import { getRequest } from "services/api";
import Swal from "sweetalert2";
import { LIMIT_DATA_PER_PAGE } from "utils/constants";
import { buildQueryString } from "utils/queryHelpers";
import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";

const TransactionContext = createContext();

export const TransactionProvider = ({ children }) => {
  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    filters: {
      payment_method_id: "",
      cashier_session_id: "",
      location: "",
      inserted_at: new Date().toISOString().split("T")[0], // Default hari ini
    },
  });

  const queryString = buildQueryString({
    search: filterOptions.search || "",
    page: filterOptions.currentPage,
    paginate: LIMIT_DATA_PER_PAGE,
    include: "paymentMethod,transactionItems,cashierSession.cashier,user",
    // "message": "Requested include(s) `tran` are not allowed. Allowed include(s) are `cashierSession, cashierSessionCount, cashierSessionExists, cashierSession.cashier, paymentMethod, paymentMethodCount, paymentMethodExists, user, userCount, userExists, transactionItems, transactionItemsCount, transactionItemsExists, transactionPaymentSplits, transactionPaymentSplitsCount, transactionPaymentSplitsExists`.",
    // Apply filters
    ...(filterOptions.filters.payment_method_id && {
      "filter[payment_method_id]": filterOptions.filters.payment_method_id,
    }),
    ...(filterOptions.filters.cashier_session_id && {
      "filter[cashier_session_id]": filterOptions.filters.cashier_session_id,
    }),
    ...(filterOptions.filters.location && {
      "filter[location]": filterOptions.filters.location,
    }),
    ...(filterOptions.filters.inserted_at && {
      "filter[inserted_at]": filterOptions.filters.inserted_at,
    }),
  });

  // Fetch data menggunakan TanStack Query
  const {
    data: transactions,
    error,
    isLoading,
    refetch, // Untuk manual refresh data
  } = useQuery({
    queryKey: ["transactions", filterOptions],
    queryFn: async () => {
      const response = await getRequest(`/transaction${queryString}`);
      if (!response.success) {
        throw new Error(response.message || "Failed to fetch transactions.");
      }
      return response;
    },
    keepPreviousData: true, // Agar tidak berkedip saat berpindah halaman
  });

  // Handle pencarian (Enter untuk submit)
  const handleSearch = (e) => {
    if (e.key === "Enter") {
      if (!e.target.value.trim()) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "warning",
          title: "Search input cannot be empty!",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        return;
      }
      setFilterOptions((prev) => ({
        ...prev,
        search: e.target.value,
        currentPage: 1, // Reset ke halaman pertama setelah pencarian
      }));
    }
  };

  // Update filter function
  const updateFilter = (key, value) => {
    setFilterOptions((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        [key]: value,
      },
      currentPage: 1, // Reset to first page when filter changes
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilterOptions((prev) => ({
      ...prev,
      filters: {
        payment_method_id: "",
        cashier_session_id: "",
        location: "",
        inserted_at: new Date().toISOString().split("T")[0],
      },
      currentPage: 1,
    }));
  };

  const handlePageChange = (page) => {
    setFilterOptions((prev) => ({
      ...prev,
      currentPage: page,
    }));
  };

  const totalPages = transactions?.pagination?.last_page || 1;

  const Pagination = usePaginationWithComponent({
    totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  return (
    <TransactionContext.Provider
      value={{
        transactions: transactions?.data || [],
        summaryData: transactions?.summary || {},
        isLoading,
        error,
        filterOptions,
        updateFilter,
        clearFilters,
        handleSearch,
        refetchTransaction: refetch, // Bisa dipanggil untuk refresh data
        Pagination,
      }}
    >
      {children}
    </TransactionContext.Provider>
  );
};

export const useTransaction = () => {
  const context = useContext(TransactionContext);
  if (!context) {
    throw new Error("useTransaction must be used within TransactionProvider");
  }
  return context;
};
